import React from "react"

import { WebSocketLike } from "react-use-websocket/dist/lib/types"

import { useSidekickInteraction } from "./SidekickInteractionContext"
import { useTyping } from "./TypingContext"
import { useWebsocket } from "./WebsocketContext"

export type ProgressStepName = "researching" | "thinking" | "authoring"
export type StepStatus = "pending" | "in_progress" | "complete"

export interface ProgressStep {
  name: ProgressStepName
  status: StepStatus
}

interface SidekickProgressContextType {
  isProgressVisible: boolean
  steps: ProgressStep[]
  currentStep: ProgressStepName | null
  /**
   * True while the user's speech is being transcribed (mic is open / waiting for transcription).
   * Used by the overlay variant to show a "Listening" step before the processing steps begin.
   */
  isListeningPhase: boolean
  /**
   * True if the user entered a voice/listening phase at any point during the current overlay
   * session. Used to gate the "Listening" step so it only appears for speech interactions —
   * not for typed messages or server-initiated responses like the welcome message.
   */
  showListeningStep: boolean
  /**
   * Visibility gate for the overlay variant. Becomes true as soon as the mic opens
   * (awaitingESKResponse) and stays true until isProcessing becomes false. Unlike
   * `isListeningPhase || isProgressVisible`, this never dips to false between the two phases,
   * which prevents the flash caused by the useEffect timing gap.
   */
  isOverlayVisible: boolean
  /**
   * Advance the progress indicator to a specific step based on a backend event.
   * Steps only move forward — calling with an earlier step has no effect.
   * Also ensures the progress display is visible (handles the RAG-before-transcription case).
   */
  reportBackendPhase: (name: ProgressStepName) => void
}

const STEP_SEQUENCE: ProgressStepName[] = ["researching", "thinking", "authoring"]

/**
 * Maps backend event `name` values to their corresponding step index in STEP_SEQUENCE.
 *
 * Backend sends:  STT (listening, handled separately) | RAG | LLM | TOOL (ignored) | TTS
 */
const BACKEND_EVENT_STEP_INDEX: Record<string, number> = {
  RAG: 0, // researching
  LLM: 1, // thinking
  TTS: 2, // authoring
}

const SidekickProgressContext = React.createContext<SidekickProgressContextType | null>(null)

export const SidekickProgressProvider = ({ children }: React.PropsWithChildren) => {
  const { isProcessing } = useSidekickInteraction()
  const { awaitingESKResponse, awaitingUserSentence } = useTyping()
  const { getWebSocket, isConnected } = useWebsocket()

  const [isProgressVisible, setIsProgressVisible] = React.useState(false)
  const [isOverlayVisible, setIsOverlayVisible] = React.useState(false)
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0)
  // Tracks whether the user entered a voice/listening phase in the current overlay session.
  // Resets when the overlay closes so welcome-message or typed-message interactions don't
  // accidentally show the "Listening" step on a subsequent speech interaction.
  const [showListeningStep, setShowListeningStep] = React.useState(false)

  // Tracks whether the first sentenceBuffer for the current response has been received.
  // Once true, reportBackendPhase will not re-show the progress tracker, preventing
  // subsequent TTS events (one per sentence) from re-raising the tracker mid-response.
  const sentenceReceivedRef = React.useRef(false)

  // Show overlay and reset step index as soon as awaitingESKResponse becomes true
  // (mic opens / text sent). Resetting currentStepIndex here ensures stale state from the
  // previous response (e.g. sentenceBuffer advanced it to STEP_SEQUENCE.length while the
  // sidekick was still speaking) never bleeds into the new interaction's progress display.
  React.useEffect(() => {
    if (!awaitingESKResponse) return
    setIsOverlayVisible(true)
    setCurrentStepIndex(0)
    sentenceReceivedRef.current = false
  }, [awaitingESKResponse])

  // Show processing steps when transcription arrives: awaitingESKResponse=true AND
  // awaitingUserSentence=false. This is one of two triggers for the "Researching" phase —
  // the other is the RAG backend event (handled via reportBackendPhase below).
  React.useEffect(() => {
    if (!awaitingESKResponse || awaitingUserSentence) return
    setIsProgressVisible(true)
  }, [awaitingESKResponse, awaitingUserSentence])

  // When the user's speech is being transcribed, mark that this overlay session had a
  // listening phase. This gates the "Listening" step so it only appears for voice interactions,
  // not for typed messages or server-initiated responses like the welcome message.
  React.useEffect(() => {
    if (awaitingUserSentence) {
      setShowListeningStep(true)
    }
  }, [awaitingUserSentence])

  // Hide everything when the sidekick finishes processing.
  // isProgressVisible and isOverlayVisible are included in deps so that if backend events
  // (RAG/LLM/TTS) arrive and re-raise the flags after an error reset (e.g. ERR-24 clears
  // awaitingESKResponse, making isProcessing=false, but in-flight events still fire),
  // this effect re-runs immediately and tears the overlay back down.
  React.useEffect(() => {
    if (isProcessing) return
    setIsProgressVisible(false)
    setIsOverlayVisible(false)
    setCurrentStepIndex(0)
    setShowListeningStep(false)
  }, [isProcessing, isProgressVisible, isOverlayVisible])

  /**
   * Advance the progress display to a given step. Steps only move forward.
   * Also makes the progress visible, which handles the case where the RAG event
   * arrives before the transcription sentence.
   */
  const reportBackendPhase = React.useCallback((name: ProgressStepName) => {
    // Don't re-show after the first sentence has arrived. Multiple TTS events fire for
    // multi-sentence responses, and each would otherwise re-raise the tracker.
    if (sentenceReceivedRef.current) return

    const targetIndex = STEP_SEQUENCE.indexOf(name)
    if (targetIndex === -1) return

    setIsProgressVisible(true)
    setIsOverlayVisible(true)
    setCurrentStepIndex((prev) => Math.max(prev, targetIndex))
  }, [])

  // Subscribe to WebSocket messages and drive step advances from backend events.
  // RAG → researching | LLM → thinking | TTS → authoring | sentenceBuffer → authoring complete
  React.useEffect(() => {
    let webSocketLike: WebSocketLike | null = null

    const messageHandler = (e: Event) => {
      const data = (e as MessageEvent).data
      if (typeof data !== "string") return
      try {
        const parsed = JSON.parse(data)

        // First sentenceBuffer signals that the sidekick's text is now appearing in the chat.
        // Hide the progress tracker immediately so it doesn't overlap with streaming sentences.
        // Guard: only fire when we're already on the authoring step (prev === last index).
        // This prevents stale sentenceBuffer messages from a previous/interrupted response
        // from hiding the tracker during an unrelated listening or researching phase.
        if (parsed?.type === "sentenceBuffer") {
          sentenceReceivedRef.current = true
          setCurrentStepIndex((prev) => {
            if (prev === STEP_SEQUENCE.length - 1) {
              setIsProgressVisible(false)
              setIsOverlayVisible(false)
              return STEP_SEQUENCE.length
            }
            return prev
          })
          return
        }

        if (parsed?.type !== "event" || !parsed?.name) return

        const targetIndex = BACKEND_EVENT_STEP_INDEX[parsed.name]
        if (targetIndex === undefined) return

        reportBackendPhase(STEP_SEQUENCE[targetIndex])
      } catch {
        // Not JSON or not a relevant event — ignore
      }
    }

    if (isConnected) {
      webSocketLike = getWebSocket()
      if (webSocketLike) {
        webSocketLike.addEventListener("message", messageHandler)
      }
    }

    return () => {
      if (webSocketLike) {
        webSocketLike.removeEventListener("message", messageHandler)
      }
    }
  }, [getWebSocket, isConnected, reportBackendPhase])

  const steps: ProgressStep[] = React.useMemo(
    () =>
      STEP_SEQUENCE.map((name, i) => ({
        name,
        status: !isProgressVisible
          ? "pending"
          : i < currentStepIndex
            ? "complete"
            : i === currentStepIndex
              ? "in_progress"
              : "pending",
      })),
    [isProgressVisible, currentStepIndex],
  )

  const currentStep: ProgressStepName | null =
    steps.find((s) => s.status === "in_progress")?.name ?? null

  const value = React.useMemo<SidekickProgressContextType>(
    () => ({
      isProgressVisible,
      isOverlayVisible,
      steps,
      currentStep,
      isListeningPhase: awaitingUserSentence,
      showListeningStep,
      reportBackendPhase,
    }),
    [
      isProgressVisible,
      isOverlayVisible,
      steps,
      currentStep,
      awaitingUserSentence,
      showListeningStep,
      reportBackendPhase,
    ],
  )

  return (
    <SidekickProgressContext.Provider value={value}>{children}</SidekickProgressContext.Provider>
  )
}

export const useOptionalSidekickProgress = () => React.useContext(SidekickProgressContext)

export const useSidekickProgress = () => {
  const ctx = useOptionalSidekickProgress()
  if (!ctx) throw new Error("useSidekickProgress must be used within a SidekickProgressProvider")
  return ctx
}
